import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { UpdateDocumentDetailService } from './update-document-detail.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-update-document-detail',
    templateUrl: './update-document-detail.component.html',
    styleUrls: ['./update-document-detail.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UpdateDocumentDetailComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any = {};
  
  @Input('filter-params')
  set _filter_params(filterParams: string){
    try{
      let params = safeJsonInput<any>(filterParams, null, 'update-document-detail.parse1');
      if(Object.keys(params).length > 0){
        this.filterParams = params;
        this.init();
      }
    }catch(e){
      console.log("Filter params input not yet stable. Still waiting to get resolved.");
    }
  }

  // 🔴 GLOBAL SETTINGS AS @INPUT - DO NOT CREATE NEW SERVICES
  @Input('base-urls') 
  set _base_urls(baseUrls: string){
    try{
      let urls: GtwElementsBaseUrlsModel = safeJsonInput<any>(baseUrls, null, 'update-document-detail.parse2');
      if(Object.keys(urls).length > 0){
        this.baseURLs = urls;
        this.init();
      }
    }catch(e){
      console.log("Input Not yet stable. Still waiting to get resolved.");
    }
  }
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  @Input('row-data')
  set _row_data(rowData: string){
    try{
      let data: any = safeJsonInput<any>(rowData, null, 'update-document-detail.parse3');
      if(Object.keys(data).length > 0){
        this.rowData = data;
        this.init();
      }
    }catch(e){
      console.log("Row data input not yet stable. Still waiting to get resolved.");
    }
  }
  rowData: any = {};

  @Input('header-data') 
  set _header_data(headerData: string){
    try{
      let header = safeJsonInput<any>(headerData, null, 'update-document-detail.parse4');
      if(Object.keys(header).length > 0){
        this.globalService = header.globalParams;
        this.USER_SETTINGS = header.userSettingsData;
        this.SERVER_CONFIG = header.serverConfigData;
        this.GENERAL_CONFIG = header.generalConfigData;
        this.init();
      }
    }catch(e){
      console.log("Input Not yet stable. Still waiting to get resolved.");
    }
  }

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Update Document Details for the selected entities?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following update-change-list pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = false;
  syncSaveDtls: any[] = [];
  logged_in_user: string = '';
  modalName: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private updateDocumentDetailService: UpdateDocumentDetailService
  ) { }

  ngOnInit(): void {
    this.init();
  }

  init(): void {
    // Following update-change-list pattern - check if all required data is available
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    
    // Get modal name from globalService or filterParams
    if (this.globalService?.modalScreen?.modal_name) {
      this.modalName = this.globalService.modalScreen.modal_name;
    }
    
    // Process grid data if rowData is available
    if (this.rowData) {
      this.processGridData();
    }
  }

  processGridData(): void {
    // Process grid data exactly like AngularJS controller
    if (this.rowData != null) {
      this.syncSaveDtls = [];
      
      // Clone the grid data like AngularJS
      const tempGridData = _.clone(this.rowData);
      const gridDataMultipleSelect = _.clone(tempGridData);
      
      if (gridDataMultipleSelect != undefined) {
        for (var key in gridDataMultipleSelect) {
          // Access data property like AngularJS: vm.gridDataMultipleSelect[key].data
          const dataItem = gridDataMultipleSelect[key].data || gridDataMultipleSelect[key];
          
          // Push data item directly like AngularJS: vm.syncSaveDtls.push(vm.gridDataMultipleSelect[key].data)
          this.syncSaveDtls.push(dataItem);
        }
      }
    }
  }

  onConfirm(): void {
    this.confirmSync();
  }

  cancel(): void {
    // Following update-change-list pattern - dispatch CustomEvent to close modal
    this.showModal = false;
    
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-update-document-detail-ce')) {
      document.querySelector('gtw-update-document-detail-ce')?.dispatchEvent(updateEvent);
    }
  }
  
  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmSync(): void {
    // Follow exact AngularJS behavior
    const returnClobSettingsObj: any = {};
    const ecSaveDtls = this.syncSaveDtls;
    
    // Build combination keys string exactly like AngularJS
    const ccKeys = this.syncSaveDtls.map(item => item.COMBINATION_KEY);
    const ccKeysString = ccKeys.join(',');
    
    // Build settings object with real data (NOT hardcoded)
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['combination_key'] = ccKeysString;

    const message = "Update Document Details Successfully";
    
    // Call sendDetails function like AngularJS
    this.sendDetails(returnClobSettingsObj, ecSaveDtls, message);
  }

  sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    // Show loader in the modal - update modalConfig to trigger change detection
    this.modalConfig = { ...this.modalConfig, loading: true };
    
    // Dismiss modal immediately like AngularJS: $uibModalInstance.dismiss('cancel')
    this.cancel();
    
    // Call service method like AngularJS
    this.updateDocumentDetailService.updateDetailsEntities(
      this.GENERAL_CONFIG.base_url, // Use base_url
      returnClobSettingsObj, // Pass returnClobSettingsObj
      ecSaveDtls, // Pass ecSaveDtls array (populated from grid data)
      this.globalService, // Pass globalService
      this.filterParams, // Pass filterParams
      this.rowData // Pass rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        // Hide loader - update modalConfig to trigger change detection
        this.modalConfig = { ...this.modalConfig, loading: false };
        
        // Handle response exactly like AngularJS
        if (result.errorMessage && result.errorMessage !== 'null') {
          // Show error message
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show success message AFTER API call succeeds (like AngularJS)
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        // Hide loader on error - update modalConfig to trigger change detection
        this.modalConfig = { ...this.modalConfig, loading: false };
        
        console.error('Update Document Details error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while updating document details. Please try again.', 'gtw-snackbar--red');
      }
    );
  }
}

